import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export const DetailModal = ({ open, onClose, image, images, imageAlt, tag, title, testid, children }) => {
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const gallery = images && images.length > 1 ? images : null;

  useEffect(() => {
    if (open) setActive(0);
  }, [open, image]);

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-6"
          data-testid={testid}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="absolute inset-0 bg-[#050B14]/92 backdrop-blur-md" onClick={onClose} data-testid={`${testid}-backdrop`} />
          <motion.div
            initial={{ y: 70, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 50, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            data-lenis-prevent
            className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto overscroll-contain bg-[#0B1528] border border-[rgba(212,175,55,0.28)] shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
          >
            <button
              onClick={onClose}
              aria-label={t.details.common.close}
              data-testid={`${testid}-close-button`}
              className="absolute top-4 right-4 z-10 w-11 h-11 flex items-center justify-center bg-[#050B14]/75 backdrop-blur-md border border-[rgba(212,175,55,0.4)] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050B14] transition-colors duration-300"
            >
              <X size={18} />
            </button>
            <div className="relative aspect-[21/9] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={gallery ? gallery[active] : image}
                  src={gallery ? gallery[active] : image}
                  alt={imageAlt}
                  initial={{ opacity: 0, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid={`${testid}-image`}
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1528] via-[#0B1528]/20 to-transparent pointer-events-none" />
            </div>
            {gallery && (
              <div className="relative z-10 flex gap-2.5 px-7 sm:px-12 pt-4 overflow-x-auto" data-testid={`${testid}-gallery`}>
                {gallery.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setActive(i)}
                    data-testid={`${testid}-thumb-${i}`}
                    aria-label={`Photo ${i + 1}`}
                    className={`relative shrink-0 w-20 sm:w-24 aspect-[16/10] overflow-hidden border transition-all duration-300 ${
                      active === i ? "border-[#D4AF37] opacity-100" : "border-transparent opacity-50 hover:opacity-85"
                    }`}
                  >
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className={`px-7 sm:px-12 pb-10 sm:pb-14 relative ${gallery ? "pt-6" : "-mt-10"}`}>
              <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">{tag}</p>
              <h3 className="font-display text-3xl sm:text-5xl font-light text-[#F7F5F0] leading-[1.05] mb-8" data-testid={`${testid}-title`}>
                {title}
              </h3>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
